import { storeUrls } from '@/api/helper';
import { orderService } from '@/stores/orders/orders.service';
import { useAuthStore } from '@/stores/auth/auth.slice';
import type { QueryAPI } from '@/types/request.dto';
import { uuidv4 } from '@/utils/uuid';
import type { Socket } from 'socket.io-client';
import type { PdsOrdersGateway } from '../gateway';
import {
  buildSearchPayload,
  STATUSES_BY_FILTER,
  toHistoryRow,
} from './history';
import {
  isVisibleOnBoard,
  STATUS_BY_STAGE,
  stageFromStatus,
  toPdsOrder,
} from './order.mapper';
import { toPickupChoices, type ApiPickupRange } from './pickup-slots';
import { toModifications } from './modifications';
import type { PdsApiHistoryEntry, PdsApiOrder } from './types';
import { TEXT_MAX_LENGTH } from '../types';

const API_TIMEOUT_MS = 15_000;
const PAGE_SIZE = 100;
const SEARCH_PAGE_SIZE_MAX = 200;
const DUE_MAX_PAGES = 10;
const DUE_STATUSES = [...STATUSES_BY_FILTER.open, ...STATUSES_BY_FILTER.handed];

// Une même recherche par étape : « nouvelle » ne garde que les commandes payées ou réglées en caisse.
const TAGS_BY_STAGE = [
  ['pending+paid', 'pending+cash'],
  ['in_progress'],
  ['ready'],
];

// Le serveur refuse un texte vide ou trop long : on n'envoie rien plutôt qu'un blanc.
const clipped = (text?: string) =>
  text?.trim().slice(0, TEXT_MAX_LENGTH) || undefined;

const call = async <T>(run: (queryApi: QueryAPI) => Promise<T>) => {
  const auth = useAuthStore();
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), API_TIMEOUT_MS);
  try {
    return await run({
      requestId: uuidv4(),
      signal: abort.signal,
      jwttoken: auth.tokens.accessToken,
    });
  } finally {
    clearTimeout(timer);
  }
};

const requireData = <T>(response: { data?: T } | undefined): T => {
  if (!response?.data) throw new Error('Réponse refusée par le serveur');
  return response.data;
};

const getOrder = async (orderId: string) =>
  requireData(
    await call((queryApi) =>
      orderService.getOrderById({ orderId, withRemovedItems: true }, queryApi),
    ),
  ) as unknown as PdsApiOrder;

const fetchOrder: PdsOrdersGateway['fetchOrder'] = async (orderId) => {
  const data = await getOrder(orderId);
  return isVisibleOnBoard(data) ? toPdsOrder(data) : null;
};

const loadModifications: PdsOrdersGateway['loadModifications'] = async (
  orderId,
) =>
  toModifications(
    requireData(
      await call((queryApi) =>
        orderService.getOrderHistory<PdsApiHistoryEntry>(orderId, queryApi),
      ),
    ),
  );

const loadOrder: PdsOrdersGateway['loadOrder'] = async (orderId) =>
  toPdsOrder(await getOrder(orderId));

export const apiOrdersGateway: PdsOrdersGateway = {
  async loadOrders() {
    const storeId = useAuthStore().decodedJwt.accessToken?.restaurant_ref;
    const pages = await Promise.all(
      TAGS_BY_STAGE.map((tag) =>
        call((queryApi) =>
          orderService.searchOrder(
            {
              pagination: true,
              pageSize: PAGE_SIZE,
              restaurantId: storeId,
              serviceMode: ['to_prepare'],
              tag,
              withRemovedItems: true,
            },
            queryApi,
          ),
        ),
      ),
    );
    return pages
      .flatMap((page) => requireData(page) as unknown as PdsApiOrder[])
      .filter(isVisibleOnBoard)
      .map(toPdsOrder);
  },

  async loadDeliveredOrders(from, to) {
    const storeId = useAuthStore().decodedJwt.accessToken?.restaurant_ref;
    const response = await call((queryApi) =>
      orderService.searchOrder(
        {
          pagination: true,
          pageSize: SEARCH_PAGE_SIZE_MAX,
          restaurantId: storeId,
          status: STATUSES_BY_FILTER.handed,
          dueFrom: new Date(from).toISOString(),
          dueTo: new Date(to).toISOString(),
          withRemovedItems: true,
        },
        queryApi,
      ),
    );
    return (requireData(response) as unknown as PdsApiOrder[]).map(toPdsOrder);
  },

  async loadOrdersDue(from, to) {
    const storeId = useAuthStore().decodedJwt.accessToken?.restaurant_ref;
    const found: PdsApiOrder[] = [];
    // Une période longue dépasse une page : on lit page par page jusqu'au total annoncé.
    for (let page = 1; page <= DUE_MAX_PAGES; page++) {
      const response = await call((queryApi) =>
        orderService.searchOrder(
          {
            pagination: true,
            page,
            pageSize: SEARCH_PAGE_SIZE_MAX,
            restaurantId: storeId,
            status: DUE_STATUSES,
            dueFrom: new Date(from).toISOString(),
            dueTo: new Date(to).toISOString(),
            withRemovedItems: true,
          },
          queryApi,
        ),
      );
      const batch = requireData(response) as unknown as PdsApiOrder[];
      found.push(...batch);
      if (!batch.length || found.length >= (response.total ?? found.length))
        break;
    }
    return found.filter(isVisibleOnBoard).map(toPdsOrder);
  },

  fetchOrder,
  loadOrder,
  loadModifications,

  async searchHistory(query) {
    const storeId = useAuthStore().decodedJwt.accessToken?.restaurant_ref;
    const payload = buildSearchPayload(query, storeId, Date.now());
    const response = await call((queryApi) =>
      orderService.searchOrder(payload, queryApi),
    );
    const orders = requireData(response) as unknown as PdsApiOrder[];
    return {
      rows: orders.map(toHistoryRow),
      total: response.total ?? orders.length,
    };
  },

  async setStage(orderId, stage, reason, clientMessage) {
    requireData(
      await call((queryApi) =>
        orderService.updateOrder(
          {
            orderId,
            status: STATUS_BY_STAGE[stage] as never,
            reason,
            clientMessage: clipped(clientMessage),
          },
          queryApi,
        ),
      ),
    );
  },

  async setItemPrepared(orderId, itemId, prepared) {
    requireData(
      await call((queryApi) =>
        orderService.updateItemsPreparation(
          { orderId, itemIds: [itemId], status: prepared ? 'ready' : 'todo' },
          queryApi,
        ),
      ),
    );
  },

  async adjustLines(orderId, adjustments, clientMessage, reason) {
    requireData(
      await call((queryApi) =>
        orderService.adjustOrderLines(
          {
            orderId,
            adjustments,
            clientMessage: clipped(clientMessage),
            reason: clipped(reason),
          },
          queryApi,
        ),
      ),
    );
  },

  async loadPickupChoices(date) {
    const storeId = useAuthStore().decodedJwt.accessToken?.restaurant_ref;
    if (!storeId) throw new Error('Commerce inconnu');
    const url = storeUrls.getPickupSlots(storeId);
    url.searchParams.set('date', date);
    url.searchParams.set('target', 'click_and_collect');
    const response = await call(async ({ signal }) => {
      const reply = await fetch(url, { signal });
      return (await reply.json()) as { data?: ApiPickupRange[] };
    });
    return toPickupChoices(requireData(response), date, Date.now());
  },

  async changePickupTime(orderId, pickupTime) {
    requireData(
      await call((queryApi) =>
        orderService.changePickupTime(
          { orderId, pickupTime: new Date(pickupTime).toISOString() },
          queryApi,
        ),
      ),
    );
  },

  async cancelOrder(orderId, { reason, clientMessage }) {
    requireData(
      await call((queryApi) =>
        orderService.updateOrder(
          {
            orderId,
            status: 'cancelled' as never,
            reason: clipped(reason),
            clientMessage: clipped(clientMessage),
          },
          queryApi,
        ),
      ),
    );
  },

  subscribe(socket: Socket, listener) {
    const onOrderEvent = async (payload?: { order?: { id?: string } }) => {
      const id = payload?.order?.id;
      if (!id) return;
      const order = await fetchOrder(id).catch((): null => null);
      if (order) listener.onOrder(order);
    };
    const onItemsEvent = (payload?: {
      orderId: string;
      status: string;
      items: { id: string; preparationStatus: string }[];
    }) => {
      if (!payload?.orderId) return;
      listener.onItems({
        orderId: payload.orderId,
        stage: stageFromStatus(payload.status),
        items: payload.items.map((item) => ({
          id: item.id,
          prepared: item.preparationStatus === 'ready',
        })),
      });
    };

    const events = ['order:new', 'order:updated', 'order:paid'] as const;
    events.forEach((event) => socket.on(event, onOrderEvent));
    socket.on('order:items-preparation-updated', onItemsEvent);

    return () => {
      events.forEach((event) => socket.off(event, onOrderEvent));
      socket.off('order:items-preparation-updated', onItemsEvent);
    };
  },
};
